import { animate, state, style, transition, trigger } from '@angular/animations';
import { formatDate } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ChartType } from 'angular-google-charts';
import { Subscription } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';
import { CsvColumn } from '../../../csv-export/csv-export/csv-column';
import { ReportUtils } from '../../report-utils';
import { ZeitauswahlChange } from '../../zeitauswahl/zeitauswahl-change';
import { LaborGesamt } from './labor-gesamt';

@Component({
    selector: 'kiene-labor-gesamt-report',
    templateUrl: './labor-gesamt-report.component.html',
    styleUrls: ['./labor-gesamt-report.component.scss'],
    animations: [
        trigger('detailExpanded', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class LaborGesamtReportComponent implements OnInit, AfterViewInit {
    displayedColumns = [
        'labor',
        'umsatz_prozent',
        'umsatz',
        'wareneinsatz',
        'wareneinsatz_prozent',
        'rohertrag',
        'rohertrag_prozent',
        'laborkosten',
        'laborkosten_prozent',
        'gemeinkosten',
        'gemeinkosten_prozent',
        'gewinn',
        'gewinn_prozent',
    ];
    dataSource = new MatTableDataSource<LaborGesamt>();
    dataSourceVorjahr = new MatTableDataSource<LaborGesamt>();
    @ViewChild(MatSort) sort: MatSort;
    expandedElement: LaborGesamt | null;

    tierarztCtrl = new UntypedFormControl();

    rabattCtrl = new FormControl();
    ergaenzerCtrl = new FormControl();
    aktuellSubscription: Subscription;
    vorjahrSubscription: Subscription;

    zeiteintrag: ZeitauswahlChange;
    vorjahreszeitraum: ZeitauswahlChange;

    chartType = ChartType.Line;
    chartColumns = ['Zeitraum', 'Umsatz', 'Laborkosten', 'Wareneinsatz', 'Rohertrag'];
    chartData = [['', 0, 0, 0, 0]];

    csvColumns: CsvColumn[] = [];
    csvColumnsDetails: CsvColumn[] = [];
    csvFilename = 'Labor_Gesamt.csv';

    gesamtLaborkosten = 0;
    gesamtLaborkostenProzent = '0';
    gesamtGemeinkosten = 0;
    gesamtGemeinkostenProzent = '0';
    gesamtUmsatz = 0;
    gesamtWareneinsatz = 0;
    gesamtWareneinsatzProzent = '0';
    gesamtRohertrag = 0;
    gesamtRohertragProzent = '0';
    gesamtGewinn = 0;
    gesamtGewinnProzent = '0';

    gesamtLaborkostenVorjahr = 0;
    gesamtLaborkostenProzentVorjahr = '0';
    gesamtGemeinkostenVorjahr = 0;
    gesamtGemeinkostenProzentVorjahr = '0';
    gesamtUmsatzVorjahr = 0;
    gesamtWareneinsatzVorjahr = 0;
    gesamtWareneinsatzProzentVorjahr = '0';
    gesamtRohertragVorjahr = 0;
    gesamtRohertragProzentVorjahr = '0';
    gesamtGewinnVorjahr = 0;
    gesamtGewinnProzentVorjahr = '0';

    isLoading: boolean[] = [];

    constructor(
        private readonly api: KieneTableService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.csvColumns.push(new CsvColumn('umsatz', 'Umsatz'));
        this.csvColumns.push(new CsvColumn('umsatz_prozent', 'Umsatz (%)', '%'));
        this.csvColumns.push(new CsvColumn('wareneinsatz', 'Wareneinsatz'));
        this.csvColumns.push(new CsvColumn('wareneinsatz_prozent', 'Wareneinsatz (%)', '%'));
        this.csvColumns.push(new CsvColumn('rohertrag', 'Rohertrag'));
        this.csvColumns.push(new CsvColumn('rohertrag_prozent', 'Rohertrag (%)', '%'));
        this.csvColumns.push(new CsvColumn('laborkosten', 'Laborkosten'));
        this.csvColumns.push(new CsvColumn('laborkosten_prozent', 'Laborkosten (%)', '%'));
        this.csvColumns.push(new CsvColumn('gemeinkosten', 'Gemeinkosten'));
        this.csvColumns.push(new CsvColumn('gemeinkosten_prozent', 'Gemeinkosten (%)', '%'));
        this.csvColumns.push(new CsvColumn('gewinn', 'Gewinn'));
        this.csvColumns.push(new CsvColumn('gewinn_prozent', 'Gewinn (%)', '%'));

        this.csvColumnsDetails = this.csvColumns.map(column => ({ ...column }));
        this.csvColumnsDetails.unshift(new CsvColumn('zeitraum', 'Zeitraum'));
    }

    ngAfterViewInit() {
        this.dataSource.sort = this.sort;
    }

    loadData() {
        this.loadDataAktuell();
        this.loadDataVorjahr();
    }

    loadDataAktuell() {
        let params = new HttpParams();
        params = params.set('rabatt', this.rabattCtrl.value ? '1' : '0');
        params = params.set('ergaenzer', this.ergaenzerCtrl.value ? '1' : '0');
        params = params.set('von', formatDate(this.zeiteintrag.von, 'yyyy-MM-dd', 'de'));
        params = params.set('bis', formatDate(this.zeiteintrag.bis, 'yyyy-MM-dd', 'de'));

        this.aktuellSubscription?.unsubscribe();
        this.aktuellSubscription = this.api.get(this.apiBaseUrl + 'report/read_labor_gesamt.php', params).subscribe(
            response => {
                this.dataSource.data = response.records;
                this.resetChartData();
                this.berechneGesamtwerte();
            },
            error => {
                this.messageService.errorMessage('Fehler!', error);
            },
        );
    }

    loadDataVorjahr() {
        let params = new HttpParams();
        params = params.set('rabatt', this.rabattCtrl.value ? '1' : '0');
        params = params.set('ergaenzer', this.ergaenzerCtrl.value ? '1' : '0');
        params = params.set('von', formatDate(this.vorjahreszeitraum.von, 'yyyy-MM-dd', 'de'));
        params = params.set('bis', formatDate(this.vorjahreszeitraum.bis, 'yyyy-MM-dd', 'de'));

        this.vorjahrSubscription?.unsubscribe();
        this.vorjahrSubscription = this.api.get(this.apiBaseUrl + 'report/read_labor_gesamt.php', params).subscribe(
            response => {
                this.dataSourceVorjahr.data = response.records;
                this.berechneGesamtwerteVorjahr();
            },
            error => {
                this.messageService.errorMessage('Fehler!', error);
            },
        );
    }

    zeitintervalChanged(event: ZeitauswahlChange) {
        this.zeiteintrag = event;
        this.vorjahreszeitraum = ReportUtils.getVorjahreszeitraum(this.zeiteintrag);
        this.loadData();
    }

    berechneGesamtwerte() {
        this.gesamtUmsatz = 0;
        this.gesamtWareneinsatz = 0;
        this.gesamtRohertrag = 0;
        this.gesamtLaborkosten = 0;
        this.gesamtGemeinkosten = 0;
        this.gesamtGewinn = 0;
        const data = this.dataSource.data;
        for (const r of data) {
            this.gesamtUmsatz += r.umsatz ? r.umsatz : 0;
            this.gesamtWareneinsatz += r.wareneinsatz ? r.wareneinsatz : 0;
            this.gesamtRohertrag += r.rohertrag ? r.rohertrag : 0;
            this.gesamtLaborkosten += r.laborkosten ? r.laborkosten : 0;
            this.gesamtGemeinkosten += r.gemeinkosten ? r.gemeinkosten : 0;
            this.gesamtGewinn += r.gewinn ? r.gewinn : 0;
        }

        if (this.gesamtUmsatz > 0) {
            this.gesamtWareneinsatzProzent = (
                Math.round((100 / this.gesamtUmsatz) * this.gesamtWareneinsatz * 100) / 100
            ).toString();
            this.gesamtRohertragProzent = (
                Math.round((100 / this.gesamtUmsatz) * this.gesamtRohertrag * 100) / 100
            ).toString();
            this.gesamtLaborkostenProzent = (
                Math.round((100 / this.gesamtUmsatz) * this.gesamtLaborkosten * 100) / 100
            ).toString();
            this.gesamtGemeinkostenProzent = (
                Math.round((100 / this.gesamtUmsatz) * this.gesamtGemeinkosten * 100) / 100
            ).toString();
            this.gesamtGewinnProzent = (
                Math.round((100 / this.gesamtUmsatz) * this.gesamtGewinn * 100) / 100
            ).toString();
        }
    }

    berechneGesamtwerteVorjahr() {
        this.gesamtUmsatzVorjahr = 0;
        this.gesamtWareneinsatzVorjahr = 0;
        this.gesamtRohertragVorjahr = 0;
        this.gesamtLaborkostenVorjahr = 0;
        this.gesamtGemeinkostenVorjahr = 0;
        this.gesamtGewinnVorjahr = 0;
        const data = this.dataSourceVorjahr.data;
        for (const r of data) {
            this.gesamtUmsatzVorjahr += r.umsatz ? r.umsatz : 0;
            this.gesamtWareneinsatzVorjahr += r.wareneinsatz ? r.wareneinsatz : 0;
            this.gesamtRohertragVorjahr += r.rohertrag ? r.rohertrag : 0;
            this.gesamtLaborkostenVorjahr += r.laborkosten ? r.laborkosten : 0;
            this.gesamtGemeinkostenVorjahr += r.gemeinkosten ? r.gemeinkosten : 0;
            this.gesamtGewinnVorjahr += r.gewinn ? r.gewinn : 0;
        }

        if (this.gesamtUmsatzVorjahr > 0) {
            this.gesamtWareneinsatzProzentVorjahr = (
                Math.round((100 / this.gesamtUmsatzVorjahr) * this.gesamtWareneinsatzVorjahr * 100) / 100
            ).toString();
            this.gesamtRohertragProzentVorjahr = (
                Math.round((100 / this.gesamtUmsatzVorjahr) * this.gesamtRohertragVorjahr * 100) / 100
            ).toString();
            this.gesamtLaborkostenProzentVorjahr = (
                Math.round((100 / this.gesamtUmsatzVorjahr) * this.gesamtLaborkostenVorjahr * 100) / 100
            ).toString();
            this.gesamtGemeinkostenProzentVorjahr = (
                Math.round((100 / this.gesamtUmsatzVorjahr) * this.gesamtGemeinkostenVorjahr * 100) / 100
            ).toString();
            this.gesamtGewinnProzentVorjahr = (
                Math.round((100 / this.gesamtUmsatzVorjahr) * this.gesamtGewinnVorjahr * 100) / 100
            ).toString();
        }
    }

    expandVetGroupRow(element: LaborGesamt) {
        this.expandedElement = this.expandedElement === element ? null : element;
        if (this.expandedElement) {
            this.isLoading[0] = true;

            let params = new HttpParams();
            params = params.set('von', formatDate(this.zeiteintrag.von, 'yyyy-MM-dd', 'de'));
            params = params.set('bis', formatDate(this.zeiteintrag.bis, 'yyyy-MM-dd', 'de'));
            params = params.set('aggregieren', this.zeiteintrag.aggregieren);
            this.api.get(this.apiBaseUrl + 'report/read_labor_gesamt.php', params).subscribe(
                response => {
                    element.details = response.records;
                    this.isLoading[0] = false;
                    this.chartData = [];
                    for (const d of element.details) {
                        const kosten = (d.laborkosten ? d.laborkosten : 0) + (d.gemeinkosten ? d.gemeinkosten : 0);
                        this.chartData.push([
                            d.zeitraum,
                            d.umsatz ? d.umsatz : 0,
                            kosten,
                            d.wareneinsatz ? d.wareneinsatz : 0,
                            d.rohertrag ? d.rohertrag : 0,
                        ]);
                    }
                },
                error => {
                    this.messageService.errorMessage('Fehler!', error);
                    this.isLoading[0] = false;
                },
            );
        }
    }

    resetChartData() {
        this.chartData = [['', 0, 0, 0, 0]];
    }
}
